import type { CustomerInfo } from '@arhs-fleetback/types/entities/common';
import type { InterventionStatus } from '@arhs-fleetback/types/entities/interventions';

import type * as GenericTypes from 'modules/global/generic.types';
import type { FilterPayload } from 'modules/global/reducers/filterSliceAdapter';
import type { AuditableEntity } from 'modules/global/typing/common/auditable-entity';
import type { MayBe } from 'modules/global/typing/common/common';
import type { LeadStatusEnum } from 'modules/global/typing/enums/leads.enum';
import type { DetailedIntervention } from 'modules/interventions/types/projections/detailed-intervention';
import type { BasicUser } from 'modules/users/types/projections/basic-user';

// TODO: drop the mirrors below once @arhs-fleetback/types publishes entities/leads.
type ProjectedEntity = Required<Pick<AuditableEntity, 'id' | 'createdOn' | 'modifiedOn'>> &
  Pick<AuditableEntity, 'projectionType'>;

export type ReferenceStatusIntervention = ProjectedEntity & {
  referenceId: string;
  status: InterventionStatus;
};

export type PaginatedLead = ProjectedEntity & {
  dealerId?: string;
  groupId: string;
  intervention: ReferenceStatusIntervention;
  customerInfo: CustomerInfo;
  message: string;
  status: LeadStatusEnum;
  assignee?: BasicUser;
  directMessage?: ReferenceStatusIntervention;
};

export type StoredLead = Omit<PaginatedLead, 'assignee' | 'intervention' | 'directMessage'> & {
  interventionId: string;
  assigneeId?: string;
  directMessageId?: string;
};

export type LeadModalState = {
  visible: boolean;
  leadId: string | null;
};

export const LeadsTarget = {
  DASHBOARD: 'dashboard',
  INTERVENTION: 'intervention',
} as const;

export type LeadsTarget = (typeof LeadsTarget)[keyof typeof LeadsTarget];

export type LeadsMeta = GenericTypes.MetaAction & { target?: LeadsTarget };

export type LeadsDashboardFilters = {
  sort: string;
  text: string | null;
  from: string | null;
  to: string | null;
  notExist: Array<string>;
  linkedIntervention: MayBe<boolean> | null;
  status: Array<LeadStatusEnum>;
  assignedUserIds: Array<Pick<BasicUser, 'id' | 'displayName'>>;
};

export type LeadsInterventionFilters = Pick<LeadsDashboardFilters, 'sort' | 'text'>;

export type FetchLeadsParams = Partial<LeadsDashboardFilters> & {
  dealerId?: string;
  interventionId?: string;
  language?: string;
  page?: number;
  size?: number;
};

type FetchLeadsQueryParams = Omit<FetchLeadsParams, 'assignedUserIds' | 'notExist'> & {
  page: number;
  size: number;
  projection: 'page';
  assigneeIds?: Array<string>;
  notExist?: 'assigneeId';
};

export type FetchLeadsResponse = Pick<GenericTypes.PageContent<PaginatedLead>, 'content' | 'page'>;

export type AssignPayload = {
  leadId: string;
  userId?: string | null;
};

export type SetStatusPayload = {
  leadId: string;
  status: LeadStatusEnum;
};

export type CreateDirectMessagePayload = {
  leadId: string;
  referenceId: string;
};

export type FetchLeadsAction = GenericTypes.PayloadAction<GenericTypes.GetRequest<FetchLeadsQueryParams>> & {
  meta: LeadsMeta;
};

export type FetchLeadsSuccessAction = GenericTypes.PayloadAction<FetchLeadsResponse> & { meta: LeadsMeta };

export type AssignLeadAction = GenericTypes.PayloadAction<
  GenericTypes.PatchRequest<{ userId: string | null }> & { leadId: string; userId: string | null }
>;
export type LeadSuccessAction = GenericTypes.PayloadAction<{ lead: PaginatedLead }>;

export type SetLeadStatusAction = GenericTypes.PayloadAction<
  GenericTypes.PatchRequest<Pick<SetStatusPayload, 'status'>> & SetStatusPayload
>;

export type CreateDirectMessageAction = GenericTypes.PayloadAction<
  GenericTypes.PostRequest<Pick<CreateDirectMessagePayload, 'referenceId'>> & CreateDirectMessagePayload
>;
export type CreateDirectMessageSuccessAction = GenericTypes.PayloadAction<{ intervention: DetailedIntervention }>;

export type DeleteLeadAction = GenericTypes.PayloadAction<GenericTypes.DeleteRequest<undefined> & { leadId: string }>;
export type DeleteLeadSuccessAction = GenericTypes.PayloadAction<string>;

export type SetLeadsFilterAction = GenericTypes.PayloadAction<FilterPayload<LeadsDashboardFilters>> & {
  meta: LeadsMeta;
};
export type SetLeadsFiltersAction = GenericTypes.PayloadAction<Partial<LeadsDashboardFilters>> & {
  meta: LeadsMeta;
};
export type ClearLeadsFiltersAction = GenericTypes.Action<undefined> & { meta: LeadsMeta };
export type SetInterventionFiltersAction = GenericTypes.PayloadAction<Partial<LeadsInterventionFilters>> & { meta: LeadsMeta };

export type OpenLeadModalAction = GenericTypes.PayloadAction<string>;
export type CloseLeadModalAction = GenericTypes.Action<undefined>;
